import ExcelJS from 'exceljs'

// Brand colors
const PRIMARY = '1E5045'
const PRIMARY_LIGHT = 'EDF5F3'
const ACCENT_ORANGE = 'F26522'
const HEADER_BG = '0E4749'
const WHITE = 'FFFFFF'
const LIGHT_GRAY = 'F8FAFB'
const BORDER_COLOR = 'E5E7EB'
const RED_LIGHT = 'FEF2F2'

/**
 * Export a single part's full detail as a multi-sheet XLSX.
 * Sheet 1: Part Summary (traveler-style)
 * Sheet 2: All Dimensions
 * Sheet 3: GD&T Frames
 * Sheet 4: Notes
 */
export function exportSinglePartXlsx(part) {
  const wb = new ExcelJS.Workbook()
  wb.creator = 'SourceOptima'
  wb.created = new Date()

  const drawingNum = part.drawing_number || part.part_number || 'export'

  // --- Sheet 1: Part Summary ---
  const ws = wb.addWorksheet('Part Summary')
  addTitleBlock(ws, drawingNum, 2)

  const summaryFields = [
    ['Identification', 'Drawing #', part.drawing_number],
    ['', 'Part #', part.part_number],
    ['', 'Part Name', part.part_name],
    ['', 'Revision', part.revision],
    ['', 'Drawing Type', part.drawing_type],
    ['', 'Released', part.released_status],
    ['', 'Confidence', part.confidence],
    ['Material', 'Family', part.material_family],
    ['', 'Specification', part.material_normalized],
    ['', 'Raw Callout', part.material_callout_raw],
    ['', 'Condition', part.condition_temper],
    ['', 'Coating', part.coating],
    ['', 'Stock Form', part.stock_form],
    ['Geometry', 'Envelope', part.envelope_class],
    ['', 'Size Band', part.size_band],
    ['', 'Shape', part.shape_class],
    ['', 'Bounding Box (mm)', part.bbox_length_mm && part.bbox_width_mm && part.bbox_height_mm ? `${part.bbox_length_mm.toFixed(1)} × ${part.bbox_width_mm.toFixed(1)} × ${part.bbox_height_mm.toFixed(1)}` : ''],
    ['', 'Mass', part.mass_kg != null ? (part.mass_kg < 1 ? `${(part.mass_kg * 1000).toFixed(1)} g` : `${part.mass_kg.toFixed(2)} kg`) : ''],
    ['', 'Buy-to-Fly', part.buy_to_fly != null ? `${part.buy_to_fly.toFixed(1)}:1` : ''],
    ['Routing', 'Primary Process', part.primary_process],
    ['', 'Machining Route', part.machining_route],
    ['', 'Axes', part.axis_count],
    ['', 'Setups', part.setup_count],
    ['', 'Complexity', part.complexity_tier],
    ['', 'Secondary Processes', (part.secondary_processes || []).join(', ')],
    ['Tolerances', 'Tightest Linear', part.tightest_linear_mm != null ? `±${part.tightest_linear_mm.toFixed(4)} mm` : ''],
    ['', 'Tightest GD&T', part.tightest_gdt != null ? `${part.tightest_gdt.toFixed(4)} mm` : ''],
    ['', 'Dimension Count', part.dim_count],
    ['', 'GD&T Frame Count', part.gdt_frame_count],
    ['', 'Tolerance Band', part.tolerance_band],
  ]

  // Header row
  const hdr = ws.addRow(['Section', 'Field', 'Value'])
  hdr.height = 22
  hdr.eachCell(cell => {
    cell.font = { bold: true, size: 11, color: { argb: WHITE } }
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } }
    cell.alignment = { vertical: 'middle' }
  })

  let lastSection = ''
  summaryFields.forEach((r, i) => {
    const row = ws.addRow(r)
    const isNewSection = r[0] !== '' && r[0] !== lastSection
    if (r[0]) lastSection = r[0]
    row.eachCell({ includeEmpty: true }, (cell, colNum) => {
      if (isNewSection) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: PRIMARY_LIGHT } }
        if (colNum === 1) cell.font = { bold: true, size: 10, color: { argb: PRIMARY } }
      } else if (i % 2 === 0) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: LIGHT_GRAY } }
      }
      cell.border = { bottom: { style: 'hair', color: { argb: BORDER_COLOR } } }
      cell.alignment = { vertical: 'top', wrapText: colNum === 3 }
    })
  })

  ws.getColumn(1).width = 16
  ws.getColumn(2).width = 24
  ws.getColumn(3).width = 60
  ws.views = [{ state: 'frozen', ySplit: 5 }]

  // --- Sheet 2: Dimensions ---
  const dims = part.dimensions || []
  if (dims.length > 0) {
    const wsDim = wb.addWorksheet('Dimensions')
    addTitleBlock(wsDim, drawingNum, 6)

    const dimHdr = wsDim.addRow(['#', 'Nominal', 'Upper Tol', 'Lower Tol', 'Unit', 'Type', 'General?', 'Evidence'])
    dimHdr.height = 22
    dimHdr.eachCell(cell => {
      cell.font = { bold: true, size: 10, color: { argb: WHITE } }
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } }
    })

    dims.forEach((d, i) => {
      const band = Math.abs(parseFloat(d.upper_tol || 0) - parseFloat(d.lower_tol || 0))
      const isCritical = !d.tolerance_is_general && band > 0 && band <= 0.1
      const row = wsDim.addRow([i + 1, d.nominal, d.upper_tol, d.lower_tol, d.unit, d.type, d.tolerance_is_general ? 'Yes' : 'No', d.text_evidence])
      if (isCritical) {
        row.eachCell(cell => {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: RED_LIGHT } }
          cell.font = { bold: true, size: 10 }
        })
      }
      row.eachCell(cell => {
        cell.border = { bottom: { style: 'hair', color: { argb: BORDER_COLOR } } }
      })
    })

    wsDim.getColumn(1).width = 5
    wsDim.getColumn(2).width = 14
    wsDim.getColumn(3).width = 12
    wsDim.getColumn(4).width = 12
    wsDim.getColumn(5).width = 8
    wsDim.getColumn(6).width = 12
    wsDim.getColumn(7).width = 10
    wsDim.getColumn(8).width = 50
    wsDim.views = [{ state: 'frozen', ySplit: 5 }]
  }

  // --- Sheet 3: GD&T ---
  const gdts = part.gdt_frames || []
  if (gdts.length > 0) {
    const wsGdt = wb.addWorksheet('GD&T Frames')
    addTitleBlock(wsGdt, drawingNum, 5)

    const gdtHdr = wsGdt.addRow(['#', 'Symbol', 'Tolerance', 'Feature', 'Datum A', 'Datum B', 'Datum C'])
    gdtHdr.height = 22
    gdtHdr.eachCell(cell => {
      cell.font = { bold: true, size: 10, color: { argb: WHITE } }
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } }
    })

    gdts.forEach((g, i) => {
      const row = wsGdt.addRow([i + 1, g.symbol, g.tolerance, g.affected_feature || '', g.datum_primary || '', g.datum_secondary || '', g.datum_tertiary || ''])
      row.eachCell(cell => {
        cell.border = { bottom: { style: 'hair', color: { argb: BORDER_COLOR } } }
      })
    })

    wsGdt.getColumn(1).width = 5
    wsGdt.getColumn(2).width = 16
    wsGdt.getColumn(3).width = 16
    wsGdt.getColumn(4).width = 30
    wsGdt.getColumn(5).width = 10
    wsGdt.getColumn(6).width = 10
    wsGdt.getColumn(7).width = 10
  }

  // --- Sheet 4: Notes ---
  const notes = part.notes || []
  if (notes.length > 0) {
    const wsNotes = wb.addWorksheet('Notes')
    addTitleBlock(wsNotes, drawingNum, 3)

    const noteHdr = wsNotes.addRow(['#', 'Text', 'Classification'])
    noteHdr.height = 22
    noteHdr.eachCell(cell => {
      cell.font = { bold: true, size: 10, color: { argb: WHITE } }
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } }
    })

    notes.forEach((n, i) => {
      const row = wsNotes.addRow([n.number || i + 1, n.text, n.classification || ''])
      row.eachCell(cell => {
        cell.border = { bottom: { style: 'hair', color: { argb: BORDER_COLOR } } }
        cell.alignment = { wrapText: true, vertical: 'top' }
      })
    })

    wsNotes.getColumn(1).width = 5
    wsNotes.getColumn(2).width = 80
    wsNotes.getColumn(3).width = 16
  }

  // Download
  wb.xlsx.writeBuffer().then(buffer => {
    downloadBuffer(buffer, `SourceOptima_DrawingAtlas_${sanitize(drawingNum)}.xlsx`)
  })
}

/**
 * Export multiple parts as a batch XLSX.
 * Sheet 1: Summary table (one row per part, all key fields as columns)
 * Sheet 2: Dimensions (all parts' dims with part identifier column)
 */
export function exportBatchXlsx(parts) {
  const wb = new ExcelJS.Workbook()
  wb.creator = 'SourceOptima'
  wb.created = new Date()

  // --- Sheet 1: Summary ---
  const ws = wb.addWorksheet('Drawing Atlas Summary')

  // Title
  ws.addRow([`SourceOptima — Drawing Atlas Export (${parts.length} parts)`])
  ws.mergeCells(1, 1, 1, 20)
  const titleRow = ws.getRow(1)
  titleRow.height = 28
  titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: WHITE } }
  titleRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ACCENT_ORANGE } }
  titleRow.getCell(1).alignment = { vertical: 'middle' }

  ws.addRow([`Generated: ${new Date().toISOString().split('T')[0]}`])
  ws.getRow(2).getCell(1).font = { size: 10, color: { argb: '6B7280' } }
  ws.addRow([])

  const COLS = [
    'Drawing #', 'Part #', 'Part Name', 'Revision', 'Type', 'Confidence',
    'Material Family', 'Material Spec', 'Coating', 'Stock Form',
    'Envelope', 'Size Band', 'Shape', 'Bounding Box (mm)', 'Mass',
    'Primary Process', 'Route', 'Axes', 'Setups', 'Complexity',
    'Tightest Linear (mm)', 'GD&T Frames', 'Dim Count', 'Tolerance Band',
    'Filename',
  ]

  const hdr = ws.addRow(COLS)
  hdr.height = 22
  hdr.eachCell(cell => {
    cell.font = { bold: true, size: 10, color: { argb: WHITE } }
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } }
    cell.alignment = { vertical: 'middle' }
  })

  parts.forEach((p, idx) => {
    const row = ws.addRow([
      p.drawing_number || '',
      p.part_number || '',
      p.part_name || '',
      p.revision || '',
      p.drawing_type || '',
      p.confidence || '',
      p.material_family || '',
      p.material_normalized || '',
      p.coating || '',
      p.stock_form || '',
      p.envelope_class || '',
      p.size_band || '',
      p.shape_class || '',
      p.bbox_length_mm && p.bbox_width_mm && p.bbox_height_mm ? `${p.bbox_length_mm.toFixed(1)} × ${p.bbox_width_mm.toFixed(1)} × ${p.bbox_height_mm.toFixed(1)}` : '',
      p.mass_kg != null ? (p.mass_kg < 1 ? `${(p.mass_kg * 1000).toFixed(1)} g` : `${p.mass_kg.toFixed(2)} kg`) : '',
      p.primary_process || '',
      p.machining_route || '',
      p.axis_count || '',
      p.setup_count || '',
      p.complexity_tier || '',
      p.tightest_linear_mm != null ? p.tightest_linear_mm.toFixed(4) : '',
      p.gdt_frame_count || '',
      p.dim_count || '',
      p.tolerance_band || '',
      p.filename || '',
    ])
    row.eachCell(cell => {
      if (idx % 2 === 0) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: LIGHT_GRAY } }
      cell.border = { bottom: { style: 'hair', color: { argb: BORDER_COLOR } } }
      cell.alignment = { vertical: 'top' }
    })
  })

  // Auto-width approximation
  COLS.forEach((_, i) => { ws.getColumn(i + 1).width = Math.max(12, COLS[i].length + 4) })
  ws.getColumn(3).width = 35  // Part Name
  ws.getColumn(14).width = 28 // Bounding Box
  ws.getColumn(25).width = 40 // Filename
  ws.views = [{ state: 'frozen', ySplit: 4 }]

  // --- Sheet 2: All Dimensions (if any part has them) ---
  const partsWithDims = parts.filter(p => (p.dimensions || []).length > 0)
  if (partsWithDims.length > 0) {
    const wsDim = wb.addWorksheet('All Dimensions')
    const dimHdr = wsDim.addRow(['Drawing #', 'Part #', '#', 'Nominal', 'Upper Tol', 'Lower Tol', 'Unit', 'Type', 'General?', 'Evidence'])
    dimHdr.height = 22
    dimHdr.eachCell(cell => {
      cell.font = { bold: true, size: 10, color: { argb: WHITE } }
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } }
    })

    partsWithDims.forEach(p => {
      (p.dimensions || []).forEach((d, i) => {
        const band = Math.abs(parseFloat(d.upper_tol || 0) - parseFloat(d.lower_tol || 0))
        const isCritical = !d.tolerance_is_general && band > 0 && band <= 0.1
        const row = wsDim.addRow([p.drawing_number || p.filename, p.part_number || '', i + 1, d.nominal, d.upper_tol, d.lower_tol, d.unit, d.type, d.tolerance_is_general ? 'Yes' : 'No', d.text_evidence])
        if (isCritical) {
          row.eachCell(cell => { cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: RED_LIGHT } } })
        }
        row.eachCell(cell => { cell.border = { bottom: { style: 'hair', color: { argb: BORDER_COLOR } } } })
      })
    })

    wsDim.getColumn(1).width = 20
    wsDim.getColumn(2).width = 16
    wsDim.getColumn(3).width = 5
    wsDim.getColumn(4).width = 14
    wsDim.getColumn(5).width = 12
    wsDim.getColumn(6).width = 12
    wsDim.getColumn(7).width = 8
    wsDim.getColumn(8).width = 12
    wsDim.getColumn(9).width = 10
    wsDim.getColumn(10).width = 50
  }

  // Download
  wb.xlsx.writeBuffer().then(buffer => {
    downloadBuffer(buffer, `SourceOptima_DrawingAtlas_Batch_${parts.length}parts_${new Date().toISOString().split('T')[0]}.xlsx`)
  })
}

// --- Helpers ---

function addTitleBlock(ws, drawingNum, colCount) {
  ws.addRow([`SourceOptima — Drawing Atlas: ${drawingNum}`])
  ws.mergeCells(1, 1, 1, colCount)
  const r1 = ws.getRow(1)
  r1.height = 28
  r1.getCell(1).font = { bold: true, size: 14, color: { argb: WHITE } }
  r1.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ACCENT_ORANGE } }
  r1.getCell(1).alignment = { vertical: 'middle' }

  ws.addRow([`Generated: ${new Date().toISOString().split('T')[0]}`])
  ws.mergeCells(2, 1, 2, colCount)
  ws.getRow(2).getCell(1).font = { size: 10, color: { argb: '6B7280' } }

  ws.addRow([])
}

function downloadBuffer(buffer, filename) {
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function sanitize(str) {
  return (str || 'export').replace(/[^a-zA-Z0-9_-]/g, '_')
}
